import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { formatMoney } from "../../../../../shared/utils/formatMoney";
import {
  formatSalesImportDateTime,
  formatSalesImportPeriod,
} from "../utils/salesImportListModel";

const columns = [
  { key: "period", label: "Período de ventas", width: "minmax(145px, 1.2fr)" },
  { key: "date", label: "Fecha de importación", width: "minmax(170px, 1.25fr)" },
  { key: "user", label: "Importado por", width: "minmax(180px, 1.5fr)" },
  { key: "products", label: "Productos diferentes", width: "minmax(160px, 1.15fr)" },
  { key: "amount", label: "Importe de ventas", width: "minmax(160px, 1.2fr)" },
  { key: "actions", label: "Acciones", width: "minmax(90px, 0.65fr)" },
];

const gridSx = {
  display: "grid",
  gridTemplateColumns: columns.map((column) => column.width).join(" "),
  columnGap: 0.75,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

function TextCell({ children }) {
  return (
    <Typography
      noWrap
      title={typeof children === "string" ? children : undefined}
      sx={{
        color: "primary.900",
        fontSize: 14,
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      {children}
    </Typography>
  );
}

export default function SalesImportsTable({
  rows = [],
  loading = false,
  onView,
  emptyMessage = "Todavía no hay ventas importadas.",
}) {
  return (
    <Box sx={{ flex: 1, minHeight: 0, borderRadius: "20px", overflow: "hidden" }}>
      <Box sx={{ height: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box
          sx={{
            minWidth: 970,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...gridSx,
              bgcolor: "neutral.600",
              flexShrink: 0,
              overflowY: "hidden",
              scrollbarGutter: "stable",
              px: 2,
              py: 1.6,
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  color: "logoblanco",
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  textAlign: "center",
                }}
              >
                {column.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              bgcolor: "neutral.50",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              scrollbarGutter: "stable",
            }}
          >
            {rows.length ? (
              rows.map((salesImport) => {
                const period = formatSalesImportPeriod(salesImport);
                return (
                  <Box
                    key={salesImport.id}
                    sx={{
                      ...gridSx,
                      minHeight: 58,
                      px: 2,
                      py: 1.2,
                      borderBottom: "1px solid rgba(0,0,0,.06)",
                      "&:hover": { bgcolor: "rgba(0,0,0,.03)" },
                    }}
                  >
                    <TextCell>{period}</TextCell>
                    <TextCell>{formatSalesImportDateTime(salesImport.integratedAt)}</TextCell>
                    <TextCell>{salesImport.integratedBy?.name || "—"}</TextCell>
                    <TextCell>
                      {Number(salesImport.distinctProductCount).toLocaleString("es-AR")}
                    </TextCell>
                    <TextCell>{formatMoney(salesImport.totalSalesAmount)}</TextCell>
                    <Stack direction="row" sx={{ justifyContent: "center" }}>
                      <Tooltip title="Ver detalle" arrow>
                        <span>
                          <IconButton
                            type="button"
                            size="small"
                            aria-label={`Ver detalle de la importación del ${period}`}
                            disabled={loading || !onView}
                            onClick={() => onView?.(salesImport)}
                            sx={{
                              width: 31,
                              height: 31,
                              bgcolor: "neutral.50",
                              boxShadow: "0px 2px 8px rgba(0,0,0,.08)",
                              "&:hover": { bgcolor: "secondary.100" },
                            }}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 18, color: "primary.700" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography
                  sx={{ color: "neutral.700", fontWeight: 600 }}
                >
                  {loading ? "Cargando ventas importadas..." : emptyMessage}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
